import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { anzahlwort, t } from './sprache'
import type { Sprachschluessel } from './sprache'
import { achsenname } from './Aufriss'
import { ZWISCHENSUMMENARTEN } from './Zwischensummen'
import type { Achsenangabe } from './Aufriss'

/**
 * Anzeigeoptionen: was der Bericht zeigt, ohne eine Zahl zu ändern
 * (7.3 Abschnitt 11).
 *
 * **Die Vorgabe ist Vollständigkeit.** Jede Position des Plans steht im
 * Bericht, auch die leere — eine Gliederung nach § 266 HGB ist vollständig
 * oder sie ist keine. Wer sie kürzen will, sagt das hier.
 *
 * **Die Unterdrückung gilt je Dimension, nicht je Bericht.** Das ist der Kern
 * des Auftrags: Wer unter einem Konto mit Werten nach Gesellschaften aufreißt
 * und vierzehn leere Firmen sieht, will *die* weg — und das Konto behalten.
 * Ein einziger Schalter „keine Nullzeilen" könnte das nicht: Er müsste
 * entweder beides ausblenden oder beides stehen lassen.
 *
 * **Und was für die Zeilen gilt, gilt für die Spalten.** Wer die leeren
 * Gesellschaften nicht sehen will, meint sie überall: als Zeile im Aufriss und
 * als Spalte im Konzernaufriss. Zwei Haken für dieselbe Aussage wären zwei
 * Gelegenheiten, sich zu widersprechen.
 */
export type Anzeigeoptionen = {
  /**
   * Die Dimensionen, in denen leere Zeilen (und Spalten) unterdrückt werden —
   * `positionen`, `konten`, `einheiten` oder eine Zusatzachse.
   */
  ohneNull: Set<string>
  /**
   * Welche Zwischensummen das Periodenbild zeigt — `quartal`, `halbjahr`,
   * `jahr`, `zeitraum` (7.3 Abschnitt 26, R1018). Fehlt das Feld, keine.
   */
  zwischensummen?: Set<string>
}

export const KEINE_OPTIONEN: Anzeigeoptionen = { ohneNull: new Set(), zwischensummen: new Set() }

/**
 * Der Zustand steht in der Adresse und reist deshalb durch alle Bauteile —
 * über einen Zusammenhang und nicht als Eigenschaft durch sieben Ebenen.
 *
 * Ein Bericht ohne Zusammenhang (im Test, im Nachweis) bekommt die Vorgabe:
 * **alles sichtbar**. Das ist die richtige Ausfallstellung — wer nichts
 * eingestellt hat, soll nichts vermissen.
 */
const Kontext = createContext<Anzeigeoptionen>(KEINE_OPTIONEN)

export const Anzeigeoptionenkontext = Kontext.Provider

export function useAnzeigeoptionen(): Anzeigeoptionen {
  return useContext(Kontext)
}

/** Ob in dieser Dimension leere Zeilen unterdrückt werden. */
export function unterdrueckt(optionen: Anzeigeoptionen, dimension: string | null | undefined): boolean {
  return dimension !== null && dimension !== undefined && optionen.ohneNull.has(dimension)
}

/**
 * Ob eine Aufrisszelle nichts trägt.
 *
 * **Gezählt wird jede Zahl, die in der Zelle steht** — und nur Zahlen. Eine
 * Aufrisszelle besteht aus ihrer Kennung (Gesellschaft, Konto, Zusatzachse:
 * Zeichenketten) und ihren Beträgen; die Schichten und Stufenspalten stehen
 * als Zahlenreihen darin. Damit gilt derselbe Satz für den Bericht, den
 * Spiegel und die Kapitalflussrechnung, deren Zellen verschieden gebaut sind.
 */
export function istLeereZelle(zelle: unknown): boolean {
  let leer = true

  function pruefe(wert: unknown): void {
    if (typeof wert === 'number') {
      if (wert !== 0) {
        leer = false
      }
    } else if (Array.isArray(wert)) {
      wert.forEach(pruefe)
    }
  }

  Object.values((zelle ?? {}) as Record<string, unknown>).forEach(pruefe)
  return leer
}

/**
 * Ob alle uebergebenen Betraege null sind.
 *
 * Fuer die Zeilen, deren Zahlenfelder benannt sind -- eine Berichtszeile
 * traegt neben ihren Betraegen auch ihre EBENE, und die ist eine Zahl, die
 * nichts ueber ihren Inhalt sagt. Hier wird deshalb aufgezaehlt statt
 * durchsucht.
 */
export function alleNull(...werte: (number | number[] | undefined | null)[]): boolean {
  return werte.every((w) =>
    w === undefined || w === null ? true : Array.isArray(w) ? w.every((x) => x === 0) : w === 0,
  )
}

/**
 * Die Zeilen ohne die leeren — aber **eine Gruppe bleibt, solange etwas unter
 * ihr steht**.
 *
 * Eine Summenposition kann null sein, während ihre Posten es nicht sind: Zwei
 * Beträge, die sich aufheben, sind die Regel und nicht die Ausnahme. Wer die
 * Gruppe dann wegnimmt, hängt ihre Posten unter den falschen Buchstaben.
 */
export function ohneLeereZeilen<T extends { ebene: number }>(
  zeilen: readonly T[],
  istLeer: (zeile: T) => boolean,
): T[] {
  const behalten = zeilen.map((z) => !istLeer(z))

  for (let i = zeilen.length - 1; i >= 0; i -= 1) {
    if (behalten[i]) {
      continue
    }

    for (let j = i + 1; j < zeilen.length && (zeilen[j]?.ebene ?? -1) > (zeilen[i]?.ebene ?? 0); j += 1) {
      if (behalten[j]) {
        behalten[i] = true
        break
      }
    }
  }

  return zeilen.filter((_, i) => behalten[i])
}

/**
 * Die Schaltfläche und ihr Fenster.
 *
 * Sie steht neben dem Zeitwert, weil sie dieselbe Art Entscheidung
 * trägt: Sie ändert, **wie** der Bericht aussieht, nicht **was** er rechnet.
 * Das Fenster ist mit Absicht ein Fenster und keine Klappliste — es bekommt
 * weitere Gruppen, und eine Liste aus Überschriften und Haken wäre in einer
 * Klappliste eine Liste mit Zwischenüberschriften.
 */
export function Anzeigeoptionenknopf({
  optionen,
  zusatzachsen = [],
  mitBewegungsarten = false,
  aufWahl,
  nullzeilen = true,
  mitZwischensummen = false,
  aufZwischensumme,
}: {
  optionen: Anzeigeoptionen
  /** Die Zusatzachsen, die dieser Bericht anbietet (7.3 Abschnitt 2f). */
  zusatzachsen?: Achsenangabe[]
  /**
   * Ob dieser Bericht Spalten aus Bewegungsarten hat — also ein Spiegel ist
   * (R707).
   *
   * **Nur dort kann der Haken etwas tun.** Bilanz und GuV haben
   * Periodenspalten; ein vierter Haken über ihnen wäre eine Frage ohne
   * Antwort, wie sie das Fenster schon bei den Zusatzachsen vermeidet.
   */
  mitBewegungsarten?: boolean
  aufWahl: (dimension: string, an: boolean) => void
  /**
   * Ob die Gruppe *Leere Zeilen ausblenden* dasteht. In der
   * Kapitalflussrechnung nicht: Der DRS 21 hat einen festen Zeilensatz.
   */
  nullzeilen?: boolean
  /** Ob die Gruppe *Zwischensummen* dasteht — nur im Periodenbild (R1018). */
  mitZwischensummen?: boolean
  aufZwischensumme?: (art: string, an: boolean) => void
}) {
  const [offen, setOffen] = useState(false)
  const rahmen = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!offen) {
      return
    }

    function beiKlickAussen(e: MouseEvent) {
      if (!rahmen.current?.contains(e.target as Node)) {
        setOffen(false)
      }
    }

    function beiTaste(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOffen(false)
      }
    }

    window.addEventListener('mousedown', beiKlickAussen)
    window.addEventListener('keydown', beiTaste)
    return () => {
      window.removeEventListener('mousedown', beiKlickAussen)
      window.removeEventListener('keydown', beiTaste)
    }
  }, [offen])

  // Die drei Achsen, die jeder Bericht hat — dazu, was er an Zusatzachsen
  // anbietet. Eine Achse, die in diesen Läufen keine Zeile trägt, steht nicht
  // im Fenster: Ein Haken, der nichts tun kann, ist eine Frage ohne Antwort.
  const dimensionen: { schluessel: string; name: string; erlaeuterung: string }[] = [
    {
      schluessel: 'positionen',
      name: t('anzeige.null.positionen'),
      erlaeuterung: t('anzeige.null.positionen.erlaeuterung'),
    },
    {
      schluessel: 'konten',
      name: t('anzeige.null.konten'),
      erlaeuterung: t('anzeige.null.konten.erlaeuterung'),
    },
    {
      schluessel: 'einheiten',
      name: t('anzeige.null.einheiten'),
      erlaeuterung: t('anzeige.null.einheiten.erlaeuterung'),
    },
    // **Die vierte Dimension steht quer zu den drei anderen** (R707): Sie
    // nimmt keine Zeilen weg, sondern Spalten. Der Spiegel zeigt seit dem
    // 2026-09-22 seinen ganzen Bewegungsartenkatalog (R706) -- damit eine neu
    // angelegte Art sofort dasteht -- und hier wird gekuerzt.
    ...(mitBewegungsarten
      ? [
          {
            schluessel: 'bewegungsarten',
            name: t('anzeige.null.bewegungsarten'),
            erlaeuterung: t('anzeige.null.bewegungsarten.erlaeuterung'),
          },
        ]
      : []),
    // Die Zusatzachsen heissen, wie sie im Menue heissen -- „Partner“ traegt
    // seinen Namen aus der Sprachdatei, eine Dimension des Kunden ihren
    // eigenen (B9).
    ...zusatzachsen
      .filter((a) => a.belegt)
      .map((a) => ({
        schluessel: a.schluessel,
        name: achsenname(a.schluessel, zusatzachsen),
        erlaeuterung: t('anzeige.null.achse.erlaeuterung'),
      })),
  ]

  const zwischen = optionen.zwischensummen ?? new Set<string>()
  const anzahl =
    (nullzeilen ? dimensionen.filter((d) => optionen.ohneNull.has(d.schluessel)).length : 0) +
    (mitZwischensummen ? ZWISCHENSUMMENARTEN.filter((a) => zwischen.has(a)).length : 0)

  return (
    <div className="feld zusatzfeld anzeigeoptionen" ref={rahmen}>
      <label id="anzeigeoptionen-beschriftung">{t('anzeige.titel')}</label>
      <button
        type="button"
        className="zusatzknopf"
        aria-expanded={offen}
        aria-haspopup="dialog"
        aria-labelledby="anzeigeoptionen-beschriftung"
        onClick={() => setOffen(!offen)}
      >
        <span>{anzahl === 0 ? t('anzeige.keine') : t('anzeige.mehrere', { anzahl: anzahlwort(anzahl) })}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {offen && (
        <div className="optionenfenster" role="dialog" aria-labelledby="anzeigeoptionen-beschriftung">
          {nullzeilen && (
            <>
              <h3>{t('anzeige.null.titel')}</h3>

              {dimensionen.map((d) => (
                <label key={d.schluessel}>
                  <input
                    type="checkbox"
                    checked={optionen.ohneNull.has(d.schluessel)}
                    onChange={(e) => aufWahl(d.schluessel, e.target.checked)}
                  />
                  <span>
                    <span className="name">{d.name}</span>
                    <span className="erlaeuterung">{d.erlaeuterung}</span>
                  </span>
                </label>
              ))}
            </>
          )}

          {/* **Zwischensummen im Periodenbild** (7.3 Abschnitt 26, R1018) —
              mehrere zugleich, je eine Spalte hinter ihrer letzten Periode. */}
          {mitZwischensummen && (
            <>
              <h3>{t('anzeige.zwischen.titel')}</h3>

              {ZWISCHENSUMMENARTEN.map((art) => (
                <label key={art}>
                  <input
                    type="checkbox"
                    checked={zwischen.has(art)}
                    onChange={(e) => aufZwischensumme?.(art, e.target.checked)}
                  />
                  <span>
                    <span className="name">{t(`anzeige.zwischen.${art}` as Sprachschluessel)}</span>
                  </span>
                </label>
              ))}

              <p className="erlaeuterung">{t('anzeige.zwischen.erlaeuterung')}</p>
            </>
          )}
        </div>
      )}
    </div>
  )
}
